import { Form, Button } from "react-bootstrap";
import { useEffect, useState } from "react";
import { CourseState, CourseStatus } from "./scheduler";

type Props = {
  allClassrooms?: any[];
  placedCourse: any;
  slotId: string;
  day: any;
  placedCourses: any[];
  slots: any[];
  setSlots: (params: any[]) => void;
  setPlacedCourses: (params: any) => void;
  seances: any[];
  onSessionCountChange: (count: number) => void;
  onClassroomConflict: (
    requestedRoomId: string,
    conflictingPlacement: any,
    targetSlotId: string,
  ) => void;
};

// vérifier la disponibilité de la salle de classe
export const checkClassroomAvailability = (
  slots: any[],
  placedCourses: any[],
  day: any,
  course: any,
  classroomId: string,
  seanceStart: number,
  dayId: string,
  ignoredPlacementSlotsIds?: string[],
): { isOccupied: boolean; existConflict: any } => {
  if (!classroomId) return { isOccupied: false, existConflict: undefined };

  const slotsToCheck = slots.filter(
    (s) =>
      s.dayId === dayId &&
      s.seanceIndex >= seanceStart &&
      s.seanceIndex < seanceStart + (course.seance_num || 0) &&
      s.courseId !== "" &&
      s.courseId !== undefined,
  );
  let existConflict = undefined;
  const isClassroomOccupied = slotsToCheck.some((slot) => {
    if (!slot.courseId) return false;
    existConflict = placedCourses.find(
      (placement) => {
        const isIgnoredPlacement =
          !!ignoredPlacementSlotsIds &&
          ignoredPlacementSlotsIds.length === placement.slotsIds.length &&
          ignoredPlacementSlotsIds.every((id) => placement.slotsIds.includes(id));
        if (
          isIgnoredPlacement ||
          placement.course.id !== slot.courseId ||
          placement.classroomId !== classroomId ||
          placement.day_course !== day.id
        ) {
          return false;
        }

        const duration = placement.seance_num ?? placement.course.seance_num ?? 1;
        return placement.slotsIds.some((anchorSlotId: string) => {
          const anchor = slots.find((candidate) => candidate.id === anchorSlotId);
          return (
            anchor?.dayId === dayId &&
            slot.seanceIndex >= anchor.seanceIndex &&
            slot.seanceIndex < anchor.seanceIndex + duration
          );
        });
      },
    );
    return existConflict !== undefined;
  });

  return { isOccupied: isClassroomOccupied, existConflict };
};

const CourseBubble = ({
  allClassrooms,
  placedCourse,
  slotId,
  day,
  seances,
  placedCourses,
  slots,
  setPlacedCourses,
  setSlots,
  onSessionCountChange,
  onClassroomConflict,
}: Props) => {
  const effectiveSessionCount = placedCourse.seance_num ?? placedCourse.course.seance_num ?? 1;

  // Fonction pour Supprimer un cours de l'emploi du temps
  const handleDelete = () => {
    const courseWithoutDeleted = placedCourses.filter(
      (c) =>
        !(
          c.course.id === placedCourse.course.id && c.slotsIds.includes(slotId)
        ),
    );
    setPlacedCourses(courseWithoutDeleted);

    const slotsWithoutDeleted = slots.map((s) => {
      if (
        s.courseId === placedCourse.course.id &&
        s.seanceIndex + 1 >= seances[0].index &&
        s.seanceIndex + 1 <= seances[seances.length - 1].index
      )
        s.courseId = "";
      return s;
    });

    setSlots(slotsWithoutDeleted);
  };

  // Switch pour le status
  const [isCancelled, setIsCancelled] = useState(
    placedCourse.status === CourseStatus.CANCELLED,
  );
  const [cancelReason, setCancelReason] = useState(
    placedCourse.status === CourseStatus.CANCELLED
      ? placedCourse.reason_cancel || ""
      : "",
  );

  // Switch pour rattrapage
  const [isMakeup, setIsMakeup] = useState(
    placedCourse.state === CourseState.RATTRAPAGE,
  );

  useEffect(() => {
    setIsCancelled(placedCourse.status === CourseStatus.CANCELLED);
    setCancelReason(
      placedCourse.status === CourseStatus.CANCELLED
        ? placedCourse.reason_cancel || ""
        : "",
    );
    setIsMakeup(placedCourse.state === CourseState.RATTRAPAGE);
  }, [placedCourse]);

  // Fonction pour gérer le changement de status
  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsCancelled(checked);

    if (checked) {
      const newCourseState = {
        ...placedCourse,
        status: CourseStatus.CANCELLED,
        reason_cancel: cancelReason,
      };
      const updatedCourses = placedCourses.map((c) =>
        c.course.id === placedCourse.course.id && c.slotsIds.includes(slotId)
          ? newCourseState
          : c,
      );
      setPlacedCourses(updatedCourses);
    } else {
      const newCourseState = {
        ...placedCourse,
        status: CourseStatus.SCHEDULED,
        reason_cancel: "",
      };
      const updatedCourses = placedCourses.map((c) =>
        c.course.id === placedCourse.course.id && c.slotsIds.includes(slotId)
          ? newCourseState
          : c,
      );
      setPlacedCourses(updatedCourses);
    }
  };

  // Fonction pour gérer le changement d'état (rattrapage/normal)
  const handleMakeupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsMakeup(checked);

    const newCourseState = {
      ...placedCourse,
      state: checked ? CourseState.RATTRAPAGE : CourseState.NORMAL,
    };
    const updatedCourses = placedCourses.map((c) =>
      c.course.id === placedCourse.course.id && c.slotsIds.includes(slotId)
        ? newCourseState
        : c,
    );
    setPlacedCourses(updatedCourses);
  };

  // Fonction pour gérer la raison de l'annulation du cours
  const handleCancelReasonChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setCancelReason(e.target.value);
    if (isCancelled) {
      const newCourseState = {
        ...placedCourse,
        status: CourseStatus.CANCELLED,
        reason_cancel: e.target.value,
      };
      const updatedCourses = placedCourses.map((c) =>
        c.course.id === placedCourse.course.id && c.slotsIds.includes(slotId)
          ? newCourseState
          : c,
      );
      setPlacedCourses(updatedCourses);
    }
  };

  // fonction pour gérer l'assignation d'une classe
  const handleClassroomChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newClassroomId = e.target.value;
    const { isOccupied, existConflict } = checkClassroomAvailability(
      slots,
      placedCourses,
      day,
      { ...placedCourse.course, seance_num: effectiveSessionCount },
      newClassroomId,
      seances[0].index - 1,
      placedCourse.day_course,
      placedCourse.slotsIds,
    );
    if (isOccupied) {
      onClassroomConflict(newClassroomId, existConflict, slotId);
      return;
    }
    const newCourseState = {
      ...placedCourse,
      classroomId: newClassroomId,
    };
    const updatedCourses = placedCourses.map((course) =>
      course.course.id === placedCourse?.course?.id && course.slotsIds.includes(slotId)
        ? newCourseState
        : course,
    );
    setPlacedCourses(updatedCourses);
  };

  return (
    <div className="course-bubble d-flex flex-column gap-1">
      <Form.Group>
        <Form.Label><strong>Nombre de séances dans cet EDT :</strong></Form.Label>
        <Form.Control
          type="number"
          min={1}
          step={1}
          value={effectiveSessionCount}
          onChange={(event) => {
            const count = Number(event.target.value);
            if (Number.isInteger(count) && count > 0) onSessionCountChange(count);
          }}
        />
        <Form.Text>Valeur par défaut du cours : {placedCourse.course.seance_num || "non définie"} séance(s).</Form.Text>
      </Form.Group>
      <span>
        <strong>Horaire : </strong>
        {`${seances[0].start_time} - ${seances[seances.length - 1].end_time}`}
      </span>
      <span>
        <strong>Enseignant :</strong>{" "}
        {placedCourse.course.TeacherProfil.firstname +
          " " +
          placedCourse.course.TeacherProfil.lastname}
      </span>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          alignItems: "center",
        }}
      >
        <Form.Group style={{ width: "100%" }}>
          <Form.Label>Salle de classe :</Form.Label>
          <Form.Select
            value={placedCourse.classroomId || ""}
            onChange={handleClassroomChange}
          >
            <option value="">Sélectionner une salle</option>
            {allClassrooms?.map(
              (classroom) => {
                if (!classroom.statut) return null;

                const { isOccupied } = checkClassroomAvailability(
                  slots,
                  placedCourses,
                  day,
                  { ...placedCourse.course, seance_num: effectiveSessionCount },
                  classroom.id,
                  (seances[0]?.index ?? 1) - 1,
                  placedCourse.day_course,
                  placedCourse.slotsIds,
                );

                return (
                  <option
                    key={classroom.id}
                    value={classroom.id}
                    style={{
                      color: isOccupied ? "#c62828" : "#188038",
                      fontWeight: 600,
                    }}
                  >
                    {isOccupied ? "✕ Occupée" : "✓ Disponible"} — {classroom.label}
                  </option>
                );
              },
            )}
          </Form.Select>
        </Form.Group>
        <div
          className="d-flex justify-content-between"
          style={{ width: "100%" }}
        >
          <Form.Check
            type="switch"
            id={`cancel-switch-${slotId}`}
            label="Annuler"
            checked={isCancelled}
            onChange={handleStatusChange}
          />
          <Form.Check
            type="switch"
            id={`makeup-switch-${slotId}`}
            label="Rattrapage"
            checked={isMakeup}
            onChange={handleMakeupChange}
          />{" "}
        </div>
        <Button
          size="sm"
          className="w-100"
          variant="danger"
          onClick={handleDelete}
        >
          Retirer du planning
        </Button>
      </div>
      {isCancelled && (
        <div style={{ marginTop: "0.5rem" }}>
          <Form.Group>
            <div className="d-flex justify-content-between">
              <Form.Label>
                Raison de l'annulation
                <Form.Text className="text-danger"> *</Form.Text>:
              </Form.Label>
              {/*<div>
                <Input
                  aria-label="Abscent"
                  type="checkbox"
                  checked={placedCourse?.type_delay === "Abscence"}
                  onClick={() => {
                    const nextType =
                      placedCourse?.type_delay === "Abscence"
                        ? undefined
                        : "Abscence";
                    const newCourseState = {
                      ...placedCourse,
                      type_delay: nextType,
                    };
                    const updatedCourses = placedCourses.map((c) =>
                      c.course.id === placedCourse.course.id && c.slotsIds.includes(slotId)
                        ? newCourseState
                        : c
                    );
                    setPlacedCourses(updatedCourses);
                  }}
                  style={{ width: "15px", margin: "2px" }}
                />
                <Form.Label>Abscence ?</Form.Label>
              </div>*/}
            </div>
            <Form.Control
              as="textarea"
              rows={3}
              value={cancelReason}
              onChange={handleCancelReasonChange}
              placeholder="Entrez la raison ici..."
            />
          </Form.Group>
        </div>
      )}
    </div>
  );
};

export default CourseBubble;
